import {addPcbNodes, DocumentService} from "@buildwithflux/core";
import {
    type AutoLayoutIterationClientData,
    createAutoLayoutIterationClientData,
    DUMMY_ITERATION,
    type StartedAutoLayoutJob,
} from "@buildwithflux/models";
import {Logger} from "@buildwithflux/shared";
import {keyBy} from "lodash";

import {AbstractDocumentPreviewService} from "../../preview/DocumentPreviewService";
import {createBoundedQueue} from "../../stores/pcb/PcbBakedGoodsManager/createBoundedQueue";
import {getAllBakedNodes} from "../../stores/pcb/utils";
import type {AutoLayoutIterationFactory} from "../services/AutoLayoutIterationFactory";
import {createAutoLayoutVirtualNodes} from "../utils/nodeUtils";

import type {AutoLayoutBoardFetcher} from "./AutoLayoutBoardFetcher";
import type {AutoLayoutEventBus} from "./AutoLayoutEventBus";
import type {AutoLayoutIterationProcessor} from "./AutoLayoutIterationProcessor";
import type {AutoLayoutStoreService} from "./AutoLayoutStoreService";
import {DeepPcbAutoLayoutBaseService} from "./DeepPcbAutoLayoutBaseService";
import type {DeepPcbAutoLayoutEventMap} from "./DeepPcbAutoLayoutEventBus";

/**
 * DeepPCB implementation of iteration processor for the new auto-layout infrastructure.
 * Handles iteration processing and rendering using client job state management.
 */
export class DeepPcbAutoLayoutIterationProcessor
    extends DeepPcbAutoLayoutBaseService
    implements AutoLayoutIterationProcessor
{
    private renderIterationQueue = createBoundedQueue<AutoLayoutIterationClientData | undefined>();

    constructor(
        eventBus: AutoLayoutEventBus<DeepPcbAutoLayoutEventMap>,
        logger: Logger,
        private readonly autoLayoutStoreService: AutoLayoutStoreService,
        private readonly boardFetcher: AutoLayoutBoardFetcher,
        private readonly iterationFactory: AutoLayoutIterationFactory,
        private readonly documentService: DocumentService,
        private readonly documentPreviewService: AbstractDocumentPreviewService,
    ) {
        super("AutoLayoutIterationProcessor", eventBus, logger);
    }

    /**
     * Renders an iteration at target index using the client job state.
     * @param targetIndex - The index of the iteration to render
     */
    public async renderIteration(targetIndex: number): Promise<AutoLayoutIterationClientData | undefined> {
        this.logger.debug("Adding render iteration task to queue", {targetIndex});

        const task = async () => {
            this.logger.debug("Processing render iteration task", {targetIndex});

            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob) {
                this.logger.error("No active auto-layout job found");
                return;
            }

            const iterations = currentJob.iterations;
            if (targetIndex < 0 || targetIndex >= iterations.length) {
                return;
            }

            // The slider is no longer on this index, so we don't need to update
            if (targetIndex !== this.autoLayoutStoreService.getSliderPosition()) {
                return;
            }

            // No need to render again if the target index is already the current iteration index
            if (targetIndex === currentJob.currentIterationIndex) {
                return;
            }

            const targetIteration = iterations[targetIndex];
            if (!targetIteration) {
                this.logger.error("Error while rendering iteration: Target iteration not found", {
                    targetIndex,
                    iterations,
                });
                return;
            }

            // Mark loading state
            this.autoLayoutStoreService.setLoadingIteration(true);

            const abortController = new AbortController();
            const abortSignal = abortController.signal;

            const unsubscribeSliderChange = this.autoLayoutStoreService.getStore().subscribe((state) => {
                const newSliderPosition = state.sliderPosition;
                if (newSliderPosition !== targetIndex) {
                    this.logger.debug("Slider position changed, aborting iteration load", {
                        targetIndex,
                        newSliderPosition,
                    });
                    abortController.abort("Slider position changed");
                }
            });

            try {
                // 1. Fetch the board data if not already loaded
                const loadedIterationClientData = await this.getLoadedIterationData(targetIteration, abortSignal);
                if (!loadedIterationClientData) {
                    return;
                }

                // 1.5. Immediately unsubscribe from slider changes after loading is done
                unsubscribeSliderChange();

                // 2. Update with the loaded iteration data
                this.autoLayoutStoreService.updateIteration(loadedIterationClientData);

                // 2.5. Check if the slider position has changed during loading
                if (this.autoLayoutStoreService.getSliderPosition() !== targetIndex) {
                    this.logger.debug("Slider position changed after loading, skipping render", {
                        targetIndex,
                    });
                    return;
                }

                // Update the current iteration index in the client job
                this.autoLayoutStoreService.setCurrentIterationIndex(targetIndex);

                // 3. Dispatch the action to render the iteration and wait for it to be baked
                const success = await this.renderPreview(currentJob.layoutNodeUid, loadedIterationClientData);
                if (!success) {
                    throw new Error("Baking renderAutoLayoutIteration action failed");
                }

                return loadedIterationClientData;
            } catch (error) {
                this.logger.error("Error while rendering iteration", error);
            } finally {
                unsubscribeSliderChange();
                this.autoLayoutStoreService.setLoadingIteration(false);
            }
        };

        return this.renderIterationQueue.enqueue(task);
    }

    /**
     * Processes iterations from backend job and updates client job state.
     * @param backendJob - The backend job containing iteration data
     */
    public async processIterations(backendJob: StartedAutoLayoutJob): Promise<void> {
        this.logger.debug("Processing iterations from backend job", {backendJob});

        const currentJob = this.autoLayoutStoreService.getCurrentJob();
        if (!currentJob) {
            this.logger.error("No active auto-layout job found");
            return;
        }

        const receivedIterations = backendJob.iterations;
        if (receivedIterations.length === 0) {
            this.logger.debug("No iterations received from backend job");
            return;
        }

        const newIterations: AutoLayoutIterationClientData[] = [];

        // Add a dummy iteration in the first position when we receive the first iteration
        const isFirstIteration = currentJob.iterations.length === 0;
        if (isFirstIteration) {
            this.logger.debug("Adding dummy iteration as first iteration");
            newIterations.push(DUMMY_ITERATION);
            this.autoLayoutStoreService.setCurrentIterationIndex(0);
        }

        let newSliderPosition: number | undefined = undefined;
        // If the local sliderPosition is not set yet, and we see a previously
        // selected iteration hash in the job, restore local slider position
        // to point to that iteration first
        if (currentJob.currentIterationIndex === 0 && backendJob.selectedIterationHash) {
            const selectedIterationIndex = receivedIterations.findIndex(
                (iteration) => iteration.hash === backendJob.selectedIterationHash,
            );
            if (selectedIterationIndex !== -1) {
                this.logger.debug("Restoring slider position to previously selected iteration", {
                    selectedIterationIndex,
                    selectedIterationHash: backendJob.selectedIterationHash,
                });
                newSliderPosition = selectedIterationIndex + 1; // + 1 to account for the dummy iteration at index 0
            }
        }

        const existingIterations = currentJob.iterations;
        const existingIterationsCount = existingIterations.length;
        const existingIterationsByHash = keyBy(existingIterations, "hash");

        for (const receivedIteration of receivedIterations) {
            if (receivedIteration.hash in existingIterationsByHash) {
                // We've already processed this iteration
                continue;
            }

            // Create an empty iteration client data
            // Empty means we haven't loaded the actual board yet, it only has the location
            // to load the board at this point
            newIterations.push(createAutoLayoutIterationClientData(receivedIteration));
        }

        // Add the new iterations to the client job
        // Emit iteration received event if we got new iterations
        if (newIterations.length > 0) {
            this.autoLayoutStoreService.addIterations(newIterations);

            this.emit("iteration.received", {
                jobUid: currentJob.jobUid,
                iterationCount: newIterations.length,
            });
        }

        // Optionally stick slider to the end - only stick it to the end when it's already at the end
        // We only do this when we are not restoring to a previous selection
        if (
            newSliderPosition === undefined &&
            // Either it's the first iteration(s) we receive
            // Or the slider is already at the end
            (isFirstIteration || this.autoLayoutStoreService.getSliderPosition() === existingIterationsCount - 1)
        ) {
            this.logger.debug("Sticking slider position to the end with new iterations");
            newSliderPosition = existingIterationsCount + newIterations.length - 1;
        }

        // If we are selecting any iteration, render the iteration
        if (newSliderPosition !== undefined && newSliderPosition > 0) {
            this.autoLayoutStoreService.setSliderPosition(newSliderPosition);

            await this.renderIteration(newSliderPosition);

            // Emit iteration processed event
            this.emit("iteration.processed", {
                jobUid: currentJob.jobUid,
                iterationIndex: newSliderPosition,
            });
        }
    }

    public reset() {
        this.renderIterationQueue.clear();
    }

    private async getLoadedIterationData(
        iteration: AutoLayoutIterationClientData,
        abortSignal?: AbortSignal,
    ): Promise<AutoLayoutIterationClientData | undefined> {
        if (iteration.loaded) {
            return iteration;
        }

        try {
            // If the iteration is not loaded, we need to fetch it
            const board = await this.boardFetcher.fetchBoard(iteration.boardLocation, abortSignal);
            return this.iterationFactory.generateIterationClientData(iteration, board);
        } catch (error) {
            this.logger.error("Error fetching iteration board data", error);
            return undefined;
        }
    }

    private async renderPreview(layoutNodeUid: string, iteration: AutoLayoutIterationClientData) {
        const nodesToAdd = createAutoLayoutVirtualNodes(
            getAllBakedNodes(this.documentService),
            layoutNodeUid,
            Object.values(iteration.nodes),
        );

        return this.documentPreviewService.renderPreview(
            (draftState) => {
                addPcbNodes(draftState.pcbLayoutNodes, nodesToAdd);
            },
            iteration.hash,
            true,
        );
    }
}
